import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import parse from 'html-react-parser';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import CtaButton from '../ctaButton/ctaButton';
import styles from './twoColTimelineScroll.module.scss';

gsap.registerPlugin(ScrollTrigger);

function TwoColumnTimelineScroll({
  headline,
  textContent = [],
  imageContent = [],
  textAlignment,
}) {
  const { width } = useWindowDimensions();
  const isDesktop = useMemo(() => width > 860, [width]);
  const isRightAligned = textAlignment === 'right';

  const timelineRef = useRef(null);
  const photoWrapperRef = useRef(null);
  const textRefs = useRef([]);
  const photoRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      document.documentElement.style.setProperty(
        '--window-inner-height',
        `${window.innerHeight}px`
      );
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
      ScrollTrigger.refresh();
    });

    resizeObserver.observe(document.body);

    return () => resizeObserver.disconnect();
  }, []);

  const setTextRef = (el, index) => {
    if (el) textRefs.current[index] = el;
  };

  const setPhotoRef = (el, index) => {
    if (el) photoRefs.current[index] = el;
  };

  const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    if (!timelineRef.current || textRefs.current.length === 0) return undefined;

    // Reset image visibility
    photoRefs.current.forEach((photoEl, index) => {
      const img = photoEl?.querySelector('img');
      if (img) {
        gsap.set(img, {
          opacity: index === 0 ? 1 : 0,
          visibility: index === 0 ? 'visible' : 'hidden',
        });
      }
    });

    const timelineScrollTrigger = ScrollTrigger.create({
      trigger: timelineRef.current,
      start: 'top top',
      end: 'bottom bottom',
      scrub: true,
      markers: false,
      onLeave: () => {
        timelineRef.current?.classList.remove(styles.timeLineFixed);
        photoWrapperRef.current?.classList.remove(styles.allignTop);
      },
      onLeaveBack: () => {
        timelineRef.current?.classList.remove(styles.timeLineFixed);
        photoWrapperRef.current?.classList.add(styles.allignTop);
      },
      onEnterBack: () => {
        photoWrapperRef.current?.classList.add(styles.allignTop);
      },
      onUpdate: (self) => {
        if (self.progress > 0 && self.progress < 1) {
          timelineRef.current?.classList.add(styles.timeLineFixed);
        } else {
          timelineRef.current?.classList.remove(styles.timeLineFixed);
        }
      },
    });

    const textScrollTriggers = textRefs.current.map((textEl, index) => {
      const imageEl = photoRefs.current[index]?.querySelector('img');

      return ScrollTrigger.create({
        trigger: textEl,
        start: !isDesktop ? 'top top+=250' : 'top center+=145',
        end: !isDesktop ? 'bottom center' : 'bottom bottom',
        scrub: true,
        markers: false,
        onEnter: () => {
          setActiveIndex(index);
          gsap.to(imageEl, {
            opacity: 1,
            visibility: 'visible',
            duration: 0.2,
          });
        },
        onLeaveBack: () => {
          setActiveIndex(index - 1 >= 0 ? index - 1 : 0);
          gsap.to(imageEl, {
            opacity: index === 0 ? 1 : 0.3,
            visibility: index === 0 ? 'visible' : 'hidden',
            duration: 0.2,
          });
        },
      });
    });

    ScrollTrigger.refresh();

    return () => {
      timelineScrollTrigger.kill();
      textScrollTriggers.forEach((trigger) => trigger.kill());
    };
  }, [isDesktop, textContent.length]);

  const textData = () => (
    <div className={styles.textContainerWindow}>
      <div className={styles.textContainer}>
        <div className={styles.spacer} />
        {headline && <div className={styles.headline}>{parse(headline)}</div>}
        {textContent.map((text, index) => (
          <div
            key={text.headline}
            ref={(el) => setTextRef(el, index)}
            className={`${styles.textContent} ${
              index === activeIndex ? styles.textContentSpotlight : ''
            } ${text.cta ? styles.minHeight : ''}`}
          >
            <div>
              <div className={styles.textHeadline}>{parse(text.headline)}</div>
              <div className={styles.textDescription}>
                {parse(text.description)}
              </div>
              {text.cta && (
                <div className={styles.cta}>
                  <CtaButton
                    label={parse(text.cta.label)}
                    fullWidth={!isDesktop}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        <div className={styles.largeSpacer} />
      </div>
    </div>
  );

  const imageData = () => (
    <div
      className={`${styles.imageContainer} ${styles.allignTop} ${
        isRightAligned ? '' : styles.right
      }`}
      ref={photoWrapperRef}
    >
      {imageContent.map((image, index) => (
        <div
          key={image.alt || Math.random()}
          ref={(el) => setPhotoRef(el, index)}
          className={`${styles.imageWrapper} ${
            index === activeIndex ? styles.active : ''
          }`}
        >
          <img
            src={image.imageSRC}
            className={styles.image}
            alt={image.imageAlt}
          />
        </div>
      ))}
    </div>
  );

  const renderContent = () => (
    <>
      <div className={styles.halfWidth} data-testid="half-width-container">
        {isRightAligned ? imageData() : textData()}
      </div>
      <div className={styles.halfWidth} data-testid="half-width-container">
        {isRightAligned ? textData() : imageData()}
      </div>
    </>
  );

  return (
    <div className={styles.timeline} ref={timelineRef}>
      <div className={styles.contentWrapper} data-testid="content-wrapper">
        <div className={styles.containerStretch}>{renderContent()}</div>
      </div>
    </div>
  );
}

export default TwoColumnTimelineScroll;
